import type { BookableEventFormData, EventFormData } from "./event-schemas";
import type { paths } from "./api-types";

type AdminCreateEventBody = NonNullable<
  paths["/api/admin/events"]["post"]["requestBody"]
>["content"]["application/json"];

type AdminUpdateEventBody = NonNullable<
  paths["/api/admin/events/{eventId}"]["put"]["requestBody"]
>["content"]["application/json"];

type AdminEvent =
  paths["/api/admin/events/{id}"]["get"]["responses"]["200"]["content"]["application/json"]["event"];

type AdminEventSession = AdminEvent["sessions"][number];
type AdminEventPricePoint = AdminEvent["pricePoints"][number];

/**
 * Transforms form data into the API payload format.
 *
 * Note: `EventFormData` (from shared Zod schemas) uses `Date` for date fields
 * (via `z.coerce.date()`), but the generated OpenAPI `paths[...]` request-body
 * types use ISO date-time strings. This function bridges that mismatch.
 */
export function transformEventFormDataToPayload(formData: EventFormData) {
  if (!formData.isBookable) {
    // eventKind accepted by API; may be missing from generated types until regenerated
    const payload = {
      isBookable: false,
      eventKind: (formData as { eventKind?: string }).eventKind ?? "STANDARD",
      title: formData.title,
      description: formData.description,
      timezone: formData.timezone,
      location: formData.location ?? null,
      startsAt: formData.startsAt.toISOString(),
      endsAt: formData.endsAt.toISOString(),
    isPublished: formData.isPublished,
    isFeatured: formData.isFeatured,
    isCancelled: false,
      rrule: formData.isRecurring ? (formData.rrule ?? undefined) : undefined,
      isRecurring: formData.isRecurring,
      tags: formData.tags,
    } as AdminCreateEventBody;
    return payload;
  }

  const payload = {
    isBookable: true,
    eventKind: (formData as { eventKind?: string }).eventKind ?? "STANDARD",
    title: formData.title,
    description: formData.description,
    timezone: formData.timezone,
    schedule: formData.schedule ?? null,
    whatToBring: formData.whatToBring ?? null,
    location: formData.location ?? null,
    audience: formData.audience,
    requiresConsentForm: formData.requiresConsentForm,
    requiresPhotoConsent: formData.requiresPhotoConsent,
    groupsEnabled: formData.groupsEnabled,
    isPublished: formData.isPublished,
    isFeatured: formData.isFeatured,
    isCancelled: false,
    imageUrl: formData.imageUrl ?? null,
    bookingClosesAt: formData.bookingClosesAt?.toISOString() || null,
    sessions: formData.sessions.map((s: BookableEventFormData["sessions"][number]) => ({
      startsAt: s.startsAt.toISOString(),
      endsAt: s.endsAt.toISOString(),
      label: s.label || undefined,
    })),
    pricePoints: formData.pricePoints.map((pp: BookableEventFormData["pricePoints"][number]) => ({
      id: pp.id,
      label: pp.label,
      description: pp.description ?? null,
      priceCents: pp.priceCents,
      sortOrder: pp.sortOrder,
      availableFrom: pp.availableFrom?.toISOString() || null,
      availableUntil: pp.availableUntil?.toISOString() || null,
      depositCents: pp.depositCents ?? null,
      balanceDueAt: pp.balanceDueAt?.toISOString() || null,
      capacity: pp.capacity ?? null,
    })),
    tags: formData.tags,
    rrule: formData.isRecurring ? (formData.rrule ?? undefined) : undefined,
    isRecurring: formData.isRecurring,
    imagePool: formData.isRecurring ? formData.imagePool : undefined,
  } as AdminCreateEventBody;
  return payload;
}

export function buildUpdateEventPayloadFromEvent(
  event: AdminEvent,
  overrides?: {
    title?: string;
    description?: string;
    timezone?: string;
    location?: string | null;
    tags?: string[];
    eventKind?: "STANDARD" | "RACE";
    // Bookable-only
    schedule?: string | null;
    whatToBring?: string | null;
    audience?: BookableEventFormData["audience"];
    requiresConsentForm?: boolean;
    requiresPhotoConsent?: boolean;
    groupsEnabled?: boolean;
    bookingClosesAt?: string | null;
    isPublished?: boolean;
    isFeatured?: boolean;
    isCancelled?: boolean;
    imageUrl?: string | null;
    sessions?: AdminEventSession[];
    pricePoints?: AdminEventPricePoint[];
    // Non-bookable-only
    startsAt?: string;
    endsAt?: string;
    isRecurring?: boolean;
    rrule?: string | null;
  },
): AdminUpdateEventBody {
  const nextIsPublished = overrides?.isPublished ?? event.isPublished;
  const nextIsCancelled = overrides?.isCancelled ?? event.isCancelled;

  if ((event.isBookable ?? true) === false) {
    return {
      isBookable: false,
      eventKind: overrides?.eventKind ?? (event as { eventKind?: string }).eventKind ?? "STANDARD",
      title: overrides?.title ?? event.title,
      description: overrides?.description ?? event.description,
      timezone: overrides?.timezone ?? event.timezone,
      location: overrides?.location ?? (event.location ?? null),
      startsAt: overrides?.startsAt ?? event.startsAt,
      endsAt: overrides?.endsAt ?? event.endsAt,
      tags: overrides?.tags ?? event.tags.map((t) => t.tag.name),
      isRecurring: overrides?.isRecurring ?? false,
      rrule: overrides?.rrule ?? null,
    isPublished: nextIsPublished,
    isFeatured: false, // Non-bookable events cannot be featured
    isCancelled: nextIsCancelled,
    } as AdminUpdateEventBody;
  }

  const sessions = overrides?.sessions ?? event.sessions;
  const pricePoints = overrides?.pricePoints ?? event.pricePoints;

  return {
    isBookable: true,
    eventKind: overrides?.eventKind ?? (event as { eventKind?: string }).eventKind ?? "STANDARD",
    title: overrides?.title ?? event.title,
    description: overrides?.description ?? event.description,
    timezone: overrides?.timezone ?? event.timezone,
    schedule: overrides?.schedule ?? (event.schedule ?? null),
    whatToBring: overrides?.whatToBring ?? (event.whatToBring ?? null),
    location: overrides?.location ?? (event.location ?? null),
    audience: overrides?.audience ?? event.audience,
    requiresConsentForm:
      overrides?.requiresConsentForm ?? (event.requiresConsentForm ?? false),
    requiresPhotoConsent:
      overrides?.requiresPhotoConsent ?? event.requiresPhotoConsent,
    groupsEnabled: overrides?.groupsEnabled ?? (event.groupsEnabled ?? false),
    isPublished: nextIsPublished,
    isFeatured: overrides?.isFeatured ?? (event.isFeatured ?? false),
    isCancelled: nextIsCancelled,
    bookingClosesAt: overrides?.bookingClosesAt ?? (event.bookingClosesAt || null),
    imageUrl: overrides?.imageUrl ?? event.imageUrl,
    sessions: sessions.map((s) => ({
      startsAt: s.startsAt,
      endsAt: s.endsAt,
      label: s.label || undefined,
    })),
    pricePoints: pricePoints.map((pp) => ({
      id: pp.id || undefined,
      label: pp.label,
      description: pp.description || null,
      priceCents: pp.priceCents,
      sortOrder: pp.sortOrder,
      availableFrom: pp.availableFrom || null,
      availableUntil: pp.availableUntil || null,
      depositCents: pp.depositCents ?? null,
      balanceDueAt: pp.balanceDueAt || null,
      capacity: pp.capacity ?? null,
    })),
    tags: overrides?.tags ?? event.tags.map((t) => t.tag.name),
  } as AdminUpdateEventBody;
}
